import { faFolder } from '@fortawesome/pro-solid-svg-icons/faFolder';
import {
	Badge,
	Button,
	ButtonSize,
	Colour,
	ColourOutline,
	ColourStateSolid,
	FlexAlign,
	FlexGap,
	FlexJustify,
	Pill,
	PillSize,
	StackHorizontal,
	StackVertical,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { CustomCheckbox } from '@ukpostbox/frontend-framework/lib/Components/Forms/Toggle/ToggleSwitchCustom';
import { StackedThumbnails } from '@ukpostbox/frontend-framework/lib/Components/Images/Thumbnails/StackedThumbnails';
import { ThumbnailSize } from '@ukpostbox/frontend-framework/lib/Components/Images/Thumbnails/ThumbnailTypes';
import React, { memo } from 'react';
import { useNavigate } from 'react-router';
import { useSite } from '../../../../_Global/Providers/SiteInterfaceProvider';
import { useMailboxesStore } from '../../../Providers/Stores/MailboxesStore';
import { MapMailItemThumbnails } from '../../Thumbnails/MapMailItemThumbnails';
import { MailPartTypeEnum } from '../../Thumbnails/Types/ThumbnailTypes';
import { MailItemComponentType } from '../../Types/MailItemTypes';

const GridViewMailItem = memo((props: MailItemComponentType) => {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const mailBoxInfo = useMailboxesStore((state) => state.mailBoxInfo);
	const { setPdfViewer } = useSite();
	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	const HandleClick = () => {
		if (props.shiftKey !== undefined && props.shiftKey) {
			props.callBack(props.mailUuid, !props.selected);
		} else {
			navigate(
				`/mail/${props.clientFolder ? `custom-folder/${props.clientFolder.id}` : mailBoxInfo.folderSlug}/${props.mailUuid}`,
			);
		}
	};

	return (
		<div
			draggable={props.draggable}
			onDragStart={(e: any) => props.onDragStart(e)}
			onDragEnd={(e: any) => props.onDragEnd(e)}
			onDragOver={(e: any) => props.onDragOver(e)}
			className={`mailbox-item mailbox-item-block ${props.selected ? 'item-row-selected' : ''} ${!props.dateRead ? 'item-row-highlighted' : ''} ${props.dateRead ? 'item-row-muted' : ''}`}
			data-item-id={props.mailUuid}
		>
			<div
				className='item-row-checkbox'
				draggable={false}
				role='button'
				tabIndex={0}
				onKeyDown={(e) => (e.key === 'Enter' ? e.stopPropagation() : undefined)}
				onClick={(e) => e.stopPropagation()}
			>
				{props.selectable && (
					<CustomCheckbox
						onChange={(e) => props.callBack(props.mailUuid, e)}
						label={props.mailUuid}
						name={`mailItem${props.mailUuid}`}
						hideLabel
						defaultChecked={props.selected}
					/>
				)}
			</div>

			<div
				role='button'
				tabIndex={0}
				onKeyDown={(e) => (e.key === 'Enter' ? HandleClick() : undefined)}
				onClick={(e) => HandleClick()}
				className='mailbox-item-meta'
				draggable={false}
			>
				<div className='mail-item-thumbs-container'>
					<StackedThumbnails>
						<MapMailItemThumbnails
							mailType={MailPartTypeEnum['Mail-Contents']}
							files={props.files}
							onClickCallback={(e, link) => {
								e.stopPropagation();
								setPdfViewer(true, link);
							}}
							hideMissing
							size={ThumbnailSize.Aspect}
						/>
						<MapMailItemThumbnails
							mailType={MailPartTypeEnum['Mail-Envelopes']}
							files={props.files}
							onClickCallback={(e, link) => {
								e.stopPropagation();
								setPdfViewer(true, link);
							}}
							size={ThumbnailSize.Aspect}
						/>
					</StackedThumbnails>
				</div>

				<StackVertical
					align={FlexAlign.Center}
					gap={FlexGap.ExtraSmall}
					justify={FlexJustify.Start}
					extraClass='mail-item-reference'
				>
					<StackHorizontal align={FlexAlign.Center} gap={FlexGap.ExtraSmall}>
						{!props.dateRead && <Badge colour={Colour.Primary} />}

						{props.dateRead && <span className='mail-item-tracking-id'>{props.trackingId}</span>}

						{!props.dateRead && <strong className='mail-item-tracking-id'>{props.trackingId}</strong>}
					</StackHorizontal>

					{TopUpRequired && (
						<Pill
							content={t('mailItem.labels.topUpRequired')}
							size={PillSize.Small}
							colour={ColourOutline.Alert}
						/>
					)}

					{props.mailLocationId !== 20 && props.mailAction.requiresUserInput && (
						<Pill content='Item Locked' size={PillSize.Small} colour={ColourOutline.Alert} />
					)}

					{!TopUpRequired && props.daysUntilNextStorageCharge && (
						<Pill
							content={t('mailItem.labels.storageCharges', { number: props.daysUntilNextStorageCharge })}
							size={PillSize.Small}
							colour={
								props?.daysUntilNextStorageCharge && props?.daysUntilNextStorageCharge <= 5
									? ColourOutline.Alert
									: ColourOutline.Secondary
							}
						/>
					)}

					{!TopUpRequired && (
						<StackHorizontal gap={FlexGap.ExtraSmall} wrap extraClass='mailbox-item-extra'>
							{props.clientFolder?.id && mailBoxInfo.customFolderId !== props.clientFolder?.id && (
								<Button
									title={props.clientFolder?.description}
									icon={faFolder}
									colour={props.clientFolder?.colour || Colour.Secondary}
									squared
									size={ButtonSize.Small}
									actionLink={{ url: `/mail/custom-folder/${props.clientFolder.id}` }}
								/>
							)}
							{props.forwardingBatchId && (
								<Button
									title='View Batch'
									colour={ColourStateSolid.Primary}
									squared
									size={ButtonSize.Small}
									actionLink={{ url: `/mail/forwarding-batches/${props.forwardingBatchId}` }}
								/>
							)}
						</StackHorizontal>
					)}
				</StackVertical>
			</div>
		</div>
	);
});

export default GridViewMailItem;
